import {vector} from "./Vector";

export const Forces = () => {
    return {
        damping: damping,
        gravity: gravity,
        gravitation: gravitation,
        vanDerWaals: vanDerWaals,
        reflectionBottomWall: reflectionBottomWall,
        reflectionLeftWall: reflectionLeftWall,
        reflectionRightWall: reflectionRightWall,
        reflectionTopWall: reflectionTopWall,
    };
}

const damping = (k1, k2 = 0) => {
    return {
        type: "external",
        name: "Damping",
        external: function (object) {
            const speed = vector.length(object.v);
            const d1 = vector.multiply(object.v, -k1);
            const d2 = vector.multiply(object.v, -k2 * speed);
            return vector.add(d1, d2);
        }
    };
}

const gravity = (g) => {
    return {
        type: "external",
        name: "Gravity",
        external: function (object) {
            return vector.get(0, g * object.m, 0);
        }
    };
}

const reflectionBottomWall = (wallYPosition, reflectionCoefficient) => {
    return {
        type: "reflection",
        name: "Bottom wall",
        reflection: function (object, displacement) {
            const distanceBeyondWall = object.r.y + displacement.r.y - wallYPosition + object.radius;
            if (distanceBeyondWall > 0) {
                displacement.r.y = -distanceBeyondWall;
                displacement.v.y = -(1 + reflectionCoefficient) * object.v.y;
            }
            return displacement;
        }
    };
}

const reflectionLeftWall = (wallXPosition, reflectionCoefficient) => {
    return {
        type: "reflection",
        name: "Left wall",
        reflection: function (object, displacement) {
            const distanceBeyondWall = wallXPosition - (object.r.x + displacement.r.x) + object.radius;
            if (distanceBeyondWall > 0) {
                displacement.r.x = distanceBeyondWall;
                displacement.v.x = -(1 + reflectionCoefficient) * object.v.x;
            }
            return displacement;
        }
    };
}

const reflectionRightWall = (wallXPosition, reflectionCoefficient) => {
    return {
        type: "reflection",
        name: "Right wall",
        reflection: function (object, displacement) {
            const distanceBeyondWall = object.r.x + displacement.r.x - wallXPosition + object.radius;
            if (distanceBeyondWall > 0) {
                displacement.r.x = -distanceBeyondWall;
                displacement.v.x = -(1 + reflectionCoefficient) * object.v.x;
            }
            return displacement;
        }
    };
}

const reflectionTopWall = (wallYPosition, reflectionCoefficient) => {
    return {
        type: "reflection",
        name: "Top wall",
        reflection: function (object, displacement) {
            const distanceBeyondWall = wallYPosition - (object.r.y + displacement.r.y) + object.radius;
            if (distanceBeyondWall > 0) {
                displacement.r.y = distanceBeyondWall;
                displacement.v.y = -(1 + reflectionCoefficient) * object.v.y;
            }
            return displacement;
        }
    };
}

const vanDerWaals = (strength) => {
    // Van der Waal: https://en.wikipedia.org/wiki/Van_der_Waals_equation
    const getVanDerWaals = (r_d) => {
        return strength * (Math.pow(1 / r_d, 7) - 2 * Math.pow(1 / r_d, 13));
    }
    return centralForce(
        "Van der Waal",
        (distance, item, other) => {
            const d = item.radius + other.radius;
            const cutoff = 0.8;
            const r_d = distance / d;
            return r_d < cutoff ? getVanDerWaals(cutoff) : getVanDerWaals(r_d);
        });
}

const gravitation = (strength) => {
    return centralForce(
        "Gravitation",
        (distance, item, other) => strength * item.m * other.m / (distance * distance));
}

const centralForce = (name, getForceMagnitude) => {
    return {
        type: "interaction",
        name: name,
        interaction: function (other, item) {
            const dr = vector.add(other.r, vector.multiply(item.r, -1));
            const distance = Math.sqrt(vector.dot(dr, dr));
            const forceMagnitude = getForceMagnitude(distance, item, other);
            return vector.multiply(dr, forceMagnitude / distance);
        }
    };
}